import { useAtom } from "jotai";
import { FC, useEffect, useRef } from "react";
import { videoPreviewTargetAtom } from "~/atoms/videoPreview";
import { useTypedLocation } from "~/hooks/useTypedLocation";
import { FloatingVideoPlayer } from "./FloatingVideoPlayer";

/**
 * App-wide mount for the floating sub-rosa video player.
 *
 * Lives here rather than inside RecordReviewTab so every surface that lists
 * sub-rosa files can open it — the Case Documents sidebar is rendered above
 * the tab tree and also appears on the fullscreen-sidebar and document
 * pages, none of which mount RecordReviewTab.
 */
export const GlobalVideoPreview: FC = () => {
  const [target, setTarget] = useAtom(videoPreviewTargetAtom);
  const { pathname } = useTypedLocation();
  const openedPathRef = useRef(pathname);

  // Close on navigation. The player used to unmount with RecordReviewTab on
  // every route change; keeping that behavior avoids leaving one patient's
  // surveillance footage on screen after switching to another appointment.
  useEffect(() => {
    if (openedPathRef.current === pathname) return;
    openedPathRef.current = pathname;
    setTarget(null);
  }, [pathname, setTarget]);

  return (
    <FloatingVideoPlayer
      open={target !== null}
      onClose={() => setTarget(null)}
      file={target?.file ?? null}
      startTime={target?.startTime ?? null}
      anchorRect={target?.anchorRect}
    />
  );
};

export default GlobalVideoPreview;
